import Seo from "../components/Seo";
import { Award, BadgeCheck, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ENROL_URL, NQS_AREAS, NQS_ASSESSMENT_DATE } from "../lib/site";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";

const NQS = () => (
  <div data-testid="nqs-page">
    <Seo
      title="NQS Quality Rating - Exceeding | Telopea Park OSHC"
      description="Telopea Park OSHC is rated Exceeding the National Quality Standard. See our quality areas and most recent assessment results."
      path="/nqs"
    />
    <PageHeader
      testId="nqs-header"
      eyebrow="Quality & Ratings"
      title="Rated Exceeding the National Quality Standard"
      description={`Telopea Park OSHC was assessed against the National Quality Standard on ${NQS_ASSESSMENT_DATE}. Here is exactly how we were rated, and what each area means for your child.`}
    />

    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-16 sm:py-20" data-testid="nqs-overall">
      <FadeIn>
        <div className="rounded-3xl bg-navy p-10 sm:p-14 text-center shadow-lg" data-testid="nqs-overall-card">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-waratah text-white">
            <Award className="h-8 w-8" />
          </div>
          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-white/70">
            Overall rating - assessed {NQS_ASSESSMENT_DATE}
          </p>
          <p className="mt-3 font-display text-4xl sm:text-5xl font-bold text-white" data-testid="nqs-overall-rating">
            Exceeding NQS
          </p>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-white/80 leading-relaxed">
            The National Quality Standard sets a national benchmark for early childhood education and school-age care in
            Australia. An overall rating of Exceeding means our practice goes beyond the Standard&rsquo;s requirements.
          </p>
        </div>
      </FadeIn>
    </section>

    <section className="bg-surface border-y border-navy-100" data-testid="nqs-areas">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
        <FadeIn>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy" data-testid="nqs-areas-title">
            The seven Quality Areas
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-slate-600 leading-relaxed">
            Every service is rated against seven Quality Areas. Quality Areas 1 to 3 were rated Meeting NQS, and Quality
            Areas 4 to 7 were rated Exceeding NQS.
          </p>
        </FadeIn>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {NQS_AREAS.map((area, i) => (
            <FadeIn key={area.id} delay={i * 0.05}>
              <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-sm" data-testid={`nqs-area-${area.id}`}>
                <span className="font-display text-4xl font-bold text-navy-200">QA{area.id}</span>
                <h3 className="mt-3 font-display text-xl font-semibold text-navy leading-snug">{area.name}</h3>
                <span
                  className={`mt-4 inline-flex w-fit items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold ${
                    area.rating === "Exceeding NQS" ? "bg-green-100 text-green-700" : "bg-navy-50 text-navy"
                  }`}
                  data-testid={`nqs-area-${area.id}-rating`}
                >
                  {area.rating === "Exceeding NQS" ? <BadgeCheck className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                  {area.rating}
                </span>
                <p className="mt-4 text-base text-slate-600 leading-relaxed">{area.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-4xl px-5 sm:px-8 py-16 sm:py-20" data-testid="nqs-meaning">
      <FadeIn>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy">What the ratings mean</h2>
        <div className="mt-8 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm" data-testid="nqs-meaning-meeting">
            <h3 className="font-display text-xl font-semibold text-navy">Meeting NQS</h3>
            <p className="mt-2 text-base sm:text-lg text-slate-600 leading-relaxed">
              The service meets the National Quality Standard - providing quality outcomes for children across the
              requirements of that Quality Area.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm" data-testid="nqs-meaning-exceeding">
            <h3 className="font-display text-xl font-semibold text-navy">Exceeding NQS</h3>
            <p className="mt-2 text-base sm:text-lg text-slate-600 leading-relaxed">
              The service goes beyond the requirements of the National Quality Standard - with practice that is embedded
              in service operations, informed by critical reflection, and shaped by meaningful engagement with families
              and the community.
            </p>
          </div>
        </div>
        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href={ENROL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nqs-enrol-button"
            className="inline-flex items-center rounded-full bg-waratah px-8 py-4 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-waratah-600"
          >
            Enrol Now
          </a>
          <Link
            to="/about"
            data-testid="nqs-about-link"
            className="inline-flex items-center rounded-full border-2 border-navy px-8 py-4 text-base font-semibold text-navy transition-all duration-200 hover:-translate-y-0.5 hover:bg-navy hover:text-white"
          >
            Our philosophy
          </Link>
        </div>
      </FadeIn>
    </section>
  </div>
);

export default NQS;
